import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

/**
 * AdminSbtAudienceCounts — inline editor for the "B2B Audience Counts
 * Worldwide" section rendered on the SBT marketing site's Media Kit page.
 * Persists to a single document in `sbt_audience_counts` via the admin
 * endpoints. Any save goes live on the next Media Kit view (no rebuild).
 */
const REGIONS = ["APAC", "CANADA", "EMEA", "LATAM", "Others", "US"];

function toInt(v) {
  const n = parseInt(String(v ?? "").replace(/[,\s]/g, ""), 10);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

function NumCell({ value, onChange, "data-testid": testId }) {
  return (
    <Input
      type="number"
      min={0}
      value={value ?? 0}
      onChange={(e) => onChange(toInt(e.target.value))}
      className="h-8 text-right font-mono text-xs w-32"
      data-testid={testId}
    />
  );
}

export default function AdminSbtAudienceCounts() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/sbt-site/audience-counts-data");
      setData({
        grand_total: r.data.grand_total || 0,
        job_level_grand_total: r.data.job_level_grand_total || 0,
        regional_totals: { ...(r.data.regional_totals || {}) },
        job_function: (r.data.job_function || []).map((r) => ({ fn: r.fn, counts: { ...r.counts } })),
        job_level: (r.data.job_level || []).map((r) => ({ level: r.level, counts: { ...r.counts } })),
        company_size: (r.data.company_size || []).map((r) => ({ ...r })),
        top_countries: (r.data.top_countries || []).map((r) => ({ ...r })),
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load audience counts");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const grandComputed = useMemo(() => {
    if (!data) return 0;
    return REGIONS.reduce((s, r) => s + (Number(data.regional_totals[r]) || 0), 0);
  }, [data]);

  const save = async () => {
    if (!data) return;
    setSaving(true);
    try {
      await api.put("/admin/sbt-site/audience-counts-data", data);
      toast.success("Audience counts saved. Live on the Media Kit now.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
    } finally { setSaving(false); }
  };

  const updateRegionalTotal = (r, v) => setData((d) => ({ ...d, regional_totals: { ...d.regional_totals, [r]: v } }));
  const updateFn = (i, r, v) => setData((d) => {
    const rows = d.job_function.slice();
    rows[i] = { ...rows[i], counts: { ...rows[i].counts, [r]: v } };
    return { ...d, job_function: rows };
  });
  const updateLevel = (i, r, v) => setData((d) => {
    const rows = d.job_level.slice();
    rows[i] = { ...rows[i], counts: { ...rows[i].counts, [r]: v } };
    return { ...d, job_level: rows };
  });
  const updateSize = (i, k, v) => setData((d) => {
    const rows = d.company_size.slice();
    rows[i] = { ...rows[i], [k]: v };
    return { ...d, company_size: rows };
  });
  const updateCountry = (i, k, v) => setData((d) => {
    const rows = d.top_countries.slice();
    rows[i] = { ...rows[i], [k]: k === "count" ? v : v };
    return { ...d, top_countries: rows };
  });
  const addCountry = () => setData((d) => ({ ...d, top_countries: [...d.top_countries, { region: "US", country: "", count: 0 }] }));
  const removeCountry = (i) => setData((d) => ({ ...d, top_countries: d.top_countries.filter((_, x) => x !== i) }));

  if (loading || !data) {
    return <div className="p-6 text-sm text-slate-500" data-testid="sbt-audience-counts-loading">Loading audience counts…</div>;
  }

  return (
    <div className="space-y-6" data-testid="sbt-audience-counts-admin">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-slate-900" data-testid="acadmin-title">B2B Audience Counts — Media Kit</h2>
          <p className="text-xs text-slate-500 mt-1">
            Live-edits the numbers rendered on <code>/sbt-site/media-kit</code>. Save publishes instantly.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs text-slate-600">
            <span className="font-semibold">Regional sum:</span>{" "}
            <span className="font-mono" data-testid="acadmin-regional-sum">{grandComputed.toLocaleString()}</span>
          </div>
          <Button onClick={save} disabled={saving} data-testid="acadmin-save-btn">
            {saving ? "Saving…" : "Save & Publish"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 border rounded-lg p-4 bg-slate-50">
        <div>
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Grand total (hero)</label>
          <Input type="number" min={0} value={data.grand_total} onChange={(e) => setData({ ...data, grand_total: toInt(e.target.value) })} className="mt-1 font-mono" data-testid="acadmin-grand" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Job-level grand total</label>
          <Input type="number" min={0} value={data.job_level_grand_total} onChange={(e) => setData({ ...data, job_level_grand_total: toInt(e.target.value) })} className="mt-1 font-mono" data-testid="acadmin-jlgrand" />
        </div>
      </div>

      <Tabs defaultValue="regional">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="regional" data-testid="acadmin-tab-regional">Regional totals</TabsTrigger>
          <TabsTrigger value="fn"       data-testid="acadmin-tab-fn">Job Function</TabsTrigger>
          <TabsTrigger value="level"    data-testid="acadmin-tab-level">Job Level</TabsTrigger>
          <TabsTrigger value="size"     data-testid="acadmin-tab-size">Company Size</TabsTrigger>
          <TabsTrigger value="country"  data-testid="acadmin-tab-country">Top Countries</TabsTrigger>
        </TabsList>

        <TabsContent value="regional" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {REGIONS.map((r) => (
              <div key={r} className="p-3 bg-white border rounded-lg">
                <div className="text-xs font-bold uppercase tracking-wide text-slate-500">{r}</div>
                <Input type="number" min={0} className="mt-1 font-mono" value={data.regional_totals[r] ?? 0} onChange={(e) => updateRegionalTotal(r, toInt(e.target.value))} data-testid={`acadmin-region-${r}`} />
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="fn" className="mt-4">
          <div className="overflow-x-auto border rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-slate-100 sticky top-0">
                <tr>
                  <th className="p-2 text-left font-bold uppercase tracking-wide">Job Function</th>
                  {REGIONS.map((r) => <th key={r} className="p-2 text-right font-bold uppercase tracking-wide">{r}</th>)}
                </tr>
              </thead>
              <tbody>
                {data.job_function.map((row, i) => (
                  <tr key={i} className="border-t">
                    <td className="p-1.5 font-semibold text-slate-800">{row.fn}</td>
                    {REGIONS.map((r) => (
                      <td key={r} className="p-1"><NumCell value={row.counts[r]} onChange={(v) => updateFn(i, r, v)} data-testid={`acadmin-fn-${i}-${r}`} /></td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="level" className="mt-4">
          <div className="overflow-x-auto border rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-slate-100"><tr>
                <th className="p-2 text-left font-bold uppercase tracking-wide">Level</th>
                {REGIONS.map((r) => <th key={r} className="p-2 text-right font-bold uppercase tracking-wide">{r}</th>)}
              </tr></thead>
              <tbody>
                {data.job_level.map((row, i) => (
                  <tr key={i} className="border-t">
                    <td className="p-1.5 font-semibold text-slate-800">{row.level}</td>
                    {REGIONS.map((r) => (
                      <td key={r} className="p-1"><NumCell value={row.counts[r]} onChange={(v) => updateLevel(i, r, v)} data-testid={`acadmin-lvl-${i}-${r}`} /></td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="size" className="mt-4">
          <div className="overflow-x-auto border rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-slate-100"><tr>
                <th className="p-2 text-left font-bold uppercase tracking-wide">Region</th>
                <th className="p-2 text-right font-bold uppercase tracking-wide">Small (0–100)</th>
                <th className="p-2 text-right font-bold uppercase tracking-wide">Mid (101–1000)</th>
                <th className="p-2 text-right font-bold uppercase tracking-wide">Enterprise (1001+)</th>
              </tr></thead>
              <tbody>
                {data.company_size.map((row, i) => (
                  <tr key={i} className="border-t">
                    <td className="p-1.5 font-semibold text-slate-800">{row.region}</td>
                    <td className="p-1"><NumCell value={row.small} onChange={(v) => updateSize(i, "small", v)} data-testid={`acadmin-sz-${i}-small`} /></td>
                    <td className="p-1"><NumCell value={row.mid} onChange={(v) => updateSize(i, "mid", v)} data-testid={`acadmin-sz-${i}-mid`} /></td>
                    <td className="p-1"><NumCell value={row.enterprise} onChange={(v) => updateSize(i, "enterprise", v)} data-testid={`acadmin-sz-${i}-ent`} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="country" className="mt-4">
          <div className="flex justify-end mb-2">
            <Button size="sm" variant="outline" onClick={addCountry} data-testid="acadmin-add-country">+ Add country</Button>
          </div>
          <div className="overflow-x-auto border rounded-lg max-h-[520px]">
            <table className="w-full text-xs">
              <thead className="bg-slate-100 sticky top-0"><tr>
                <th className="p-2 text-left font-bold uppercase tracking-wide">Region</th>
                <th className="p-2 text-left font-bold uppercase tracking-wide">Country</th>
                <th className="p-2 text-right font-bold uppercase tracking-wide">Count</th>
                <th className="p-2 w-10"></th>
              </tr></thead>
              <tbody>
                {data.top_countries.map((row, i) => (
                  <tr key={i} className="border-t">
                    <td className="p-1">
                      <select className="h-8 text-xs border rounded px-2 bg-white" value={row.region} onChange={(e) => updateCountry(i, "region", e.target.value)} data-testid={`acadmin-cty-${i}-region`}>
                        {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </td>
                    <td className="p-1">
                      <Input className="h-8 text-xs" value={row.country} onChange={(e) => updateCountry(i, "country", e.target.value)} data-testid={`acadmin-cty-${i}-name`} />
                    </td>
                    <td className="p-1"><NumCell value={row.count} onChange={(v) => updateCountry(i, "count", v)} data-testid={`acadmin-cty-${i}-count`} /></td>
                    <td className="p-1 text-center">
                      <button className="text-red-500 hover:text-red-700 text-sm" onClick={() => removeCountry(i)} data-testid={`acadmin-cty-${i}-remove`} title="Remove">×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
